import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getAttachmentPreviewKind,
  isAttachmentImageUrl,
  isAttachmentPdfUrl,
  openAttachmentViewer,
} from '../utils/attachmentViewer';
import HelportMarkIcon from './HelportMarkIcon';

const ExternalLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14 5h5v5M10 14L19 5M19 14v5H5V5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 4v11M8 12l4 4 4-4M5 20h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const getPreviewUrl = (url) => {
  if (!url || isAttachmentImageUrl(url)) return url;
  if (isAttachmentPdfUrl(url)) {
    return url.includes('#') ? url : `${url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;
  }
  return url;
};

const AttachmentInlineViewer = ({
  label,
  url,
  items = [],
  activeIndex = 0,
  onSelectIndex,
  onClose,
  loading = false,
  loadError = false,
  missingFilename = '',
}) => {
  const [imageError, setImageError] = useState(false);
  const activeItem = items[activeIndex] || { label, url };
  const displayUrl = activeItem.url || url;
  const displayLabel = activeItem.label || label;
  const previewUrl = useMemo(() => getPreviewUrl(displayUrl), [displayUrl]);
  const previewKind = useMemo(() => getAttachmentPreviewKind(displayUrl), [displayUrl]);
  const isImagePreview = previewKind === 'image';
  const isPdfPreview = previewKind === 'pdf';
  const showError = loadError || imageError;
  const totalItems = items.length || 1;

  useEffect(() => {
    setImageError(false);
  }, [displayUrl, activeIndex]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleEscape, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape, true);
    };
  }, [onClose]);

  const handleOpenExternal = () => {
    if (!displayUrl) return;
    openAttachmentViewer(displayUrl, displayLabel);
  };

  const handleDownload = () => {
    if (!displayUrl) return;
    const link = document.createElement('a');
    link.href = displayUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return createPortal(
    <div className="attachment-modal" role="dialog" aria-modal="true" aria-label={displayLabel || 'Attachment preview'}>
      <button
        type="button"
        className="attachment-modal__backdrop"
        onClick={onClose}
        aria-label="Close attachment preview"
      />
      <div className="attachment-modal__panel">
        <header className="attachment-viewer-header">
          <div className="attachment-viewer-header__main">
            <span className="attachment-viewer-header__icon" aria-hidden="true">
              <HelportMarkIcon size={34} />
            </span>
            <div className="attachment-viewer-header__copy">
              <p className="attachment-viewer-header__eyebrow">Uploaded Attachments</p>
              <h2 className="attachment-viewer-header__title">{displayLabel}</h2>
              <p className="attachment-viewer-header__meta">
                {totalItems > 1
                  ? `${isImagePreview ? 'Image' : isPdfPreview ? 'PDF' : 'Document'} ${activeIndex + 1} of ${totalItems}`
                  : isImagePreview
                    ? 'Image preview'
                    : isPdfPreview
                      ? 'PDF document preview'
                      : 'Secure document preview'}
              </p>
            </div>
          </div>

          <div className="attachment-viewer-header__actions">
            {!loading && !showError && displayUrl && (
              <>
                <button
                  type="button"
                  className="attachment-viewer-action attachment-viewer-action--ghost"
                  onClick={handleOpenExternal}
                >
                  <ExternalLinkIcon />
                  <span>Open</span>
                </button>
                <button
                  type="button"
                  className="attachment-viewer-action attachment-viewer-action--ghost"
                  onClick={handleDownload}
                >
                  <DownloadIcon />
                  <span>Download</span>
                </button>
              </>
            )}
            <button
              type="button"
              className="attachment-viewer-action attachment-viewer-action--close"
              onClick={onClose}
              aria-label="Close attachment preview"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 7l10 10M17 7L7 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </header>

        {items.length > 1 && (
          <div className="attachment-viewer-tabs" role="tablist" aria-label="Attachment files">
            {items.map((item, index) => (
              <button
                key={`${item.label}-${index}`}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                className={`attachment-viewer-tab${index === activeIndex ? ' is-active' : ''}`}
                onClick={() => onSelectIndex?.(index)}
              >
                <span className="attachment-viewer-tab__index">{index + 1}</span>
                <span className="attachment-viewer-tab__label">{item.label}</span>
              </button>
            ))}
          </div>
        )}

        <div className={`attachment-viewer-canvas attachment-viewer-canvas--${previewKind}`}>
          {loading ? (
            <div className="attachment-viewer-state">
              <span className="attachment-viewer-spinner" aria-hidden="true" />
              <p>Loading attachment...</p>
            </div>
          ) : showError ? (
            <div className="attachment-viewer-state attachment-viewer-state--error">
              <p className="attachment-viewer-state__title">File not found or cannot be loaded</p>
              {missingFilename ? (
                <p className="attachment-viewer-state__detail">
                  Missing file: <code>{decodeURIComponent(missingFilename)}</code>
                </p>
              ) : null}
              <p className="attachment-viewer-state__detail">
                Ask the applicant to re-submit attachments, or manually upload the file to
                {' '}
                <code>public_html/uploads/</code>
                {' '}
                using the exact filename above.
              </p>
            </div>
          ) : isImagePreview ? (
            <div className="attachment-viewer-stage attachment-viewer-stage--image">
              <img
                src={displayUrl}
                alt={displayLabel}
                className="attachment-viewer-stage__image"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="attachment-viewer-stage attachment-viewer-stage--pdf">
              <iframe
                src={previewUrl}
                title={displayLabel}
                className="attachment-viewer-stage__frame"
              />
            </div>
          )}
        </div>

        <footer className="attachment-viewer-footer">
          <span>Press Esc to close</span>
          <span className="attachment-viewer-footer__dot" aria-hidden="true" />
          <span>Helport Admin Preview</span>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default AttachmentInlineViewer;
